import { SpineAnimationIds } from '../../../types/animations';
import { Locales } from '../../../types/common';
import {
  GRBannerDE,
  GRBannerEN,
  GRBannerES,
  GRBannerFR,
  GRHoux,
  GRHuntingAllStarsBackgroundImgPath,
  GRHuntingAllStarsBannerDE,
  GRHuntingAllStarsBannerEN,
  GRHuntingAllStarsBannerES,
  GRHuntingAllStarsBannerFR,
  GRHuntingAllStarsChocolateCandyImgPath,
  GRHuntingAllStarsIconImgPath,
  GRHuntingAllStarsLogoImgPath,
  GRHuntingAllStarsPrimaryButtonBackroundImgPath,
  GRHuntingAllStarsRound1BackgroundFallbackImgPath,
  GRHuntingAllStarsRound2BackgroundFallbackImgPath,
  GRHuntingAllStarsRound3BackgroundFallbackImgPath,
  GRHuntingAllStarsSecondaryButtonBackroundImgPath,
  GRIconImgPath,
  GRLogoDE,
  GRLogoEN,
  GRLogoES,
  GRLogoFR,
  GRPrimaryButtonBackroundImgPath,
  GRRound1BackgroundImgPath,
  GRRulesBackgroundImgPath,
} from '../assets/img';
import {
  GREnrollmentBackgroundImgPath,
  GRRound1BackgroundFallbackImgPath,
  GRRound2BackgroundFallbackImgPath,
  GRRound2BackgroundImgPath,
  GRRound3BackgroundFallbackImgPath,
  GRRound3BackgroundImgPath,
} from '../assets/img/gift-race/backgrounds';
import {
  GRHuntingAllStarsEggDefaultSvg,
  GRHuntingAllStarsEggFailSvg,
  GRHuntingAllStarsEggPendingSvg,
  GRHuntingAllStarsEggSuccessSvg,
} from '../assets/svg';
import { GRChipStatuses, type GRChipStatus } from '../types/chip';
import { type GRSkinId, GRSkinIds } from '../types/global';
import { type GRRound as GRRoundType, GRRounds } from '../types/round';

export type GRAssetsBySkinIdType = {
  [skinId in GRSkinId]: {
    IconPath?: string;
    Backgrounds?: {
      locked: {
        [key in GRRoundType]: string;
      };
      unlocked: {
        [key in GRRoundType]: string;
      };
      rules?: string;
      enrollment?: string;
    };
    Logos: {
      [key: string]: string;
    };
    Enrollment: {
      ListItemIconImgPath: string;
    };
    Banners: {
      [key: string]: string;
    };
    Chips?: {
      DEFAULT: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    } & {
      [key in GRChipStatus]: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    };
    RoundAnimations: {
      [key in GRRoundType]?: string;
    };
    ChipAnimations?: {
      [key in GRChipStatus]: string;
    };
    Buttons: {
      primary: string;
      secondary: string;
    };
  };
};

export const GRAssetsBySkinId: GRAssetsBySkinIdType = {
  [GRSkinIds.HUNTING_ALL_STARS]: {
    IconPath: GRHuntingAllStarsIconImgPath,
    Backgrounds: {
      locked: {
        [GRRounds.SHOE]: GRHuntingAllStarsRound3BackgroundFallbackImgPath,
        [GRRounds.BALLOON]: GRHuntingAllStarsRound2BackgroundFallbackImgPath,
        [GRRounds.SHIRT]: GRHuntingAllStarsRound1BackgroundFallbackImgPath,
      },
      unlocked: {
        [GRRounds.SHOE]: GRHuntingAllStarsBackgroundImgPath,
        [GRRounds.BALLOON]: GRHuntingAllStarsBackgroundImgPath,
        [GRRounds.SHIRT]: GRHuntingAllStarsBackgroundImgPath,
      },
      rules: GRHuntingAllStarsBackgroundImgPath,
      enrollment: GRHuntingAllStarsBackgroundImgPath,
    },
    Logos: {
      [Locales.FR]: GRHuntingAllStarsLogoImgPath,
      [Locales.ES]: GRHuntingAllStarsLogoImgPath,
      [Locales.DE]: GRHuntingAllStarsLogoImgPath,
      [Locales.EN]: GRHuntingAllStarsLogoImgPath,
    },
    Enrollment: {
      ListItemIconImgPath: GRHuntingAllStarsChocolateCandyImgPath,
    },
    Banners: {
      [Locales.FR]: GRHuntingAllStarsBannerFR,
      [Locales.ES]: GRHuntingAllStarsBannerES,
      [Locales.DE]: GRHuntingAllStarsBannerDE,
      [Locales.EN]: GRHuntingAllStarsBannerEN,
    },
    Chips: {
      DEFAULT: GRHuntingAllStarsEggDefaultSvg,
      [GRChipStatuses.LOST]: GRHuntingAllStarsEggFailSvg,
      [GRChipStatuses.PENDING]: GRHuntingAllStarsEggPendingSvg,
      [GRChipStatuses.WON]: GRHuntingAllStarsEggSuccessSvg,
    },
    RoundAnimations: {
      [GRRounds.SHOE]: SpineAnimationIds.GR_HUNTING_ALL_STARS_RABBIT_3,
      [GRRounds.BALLOON]: SpineAnimationIds.GR_HUNTING_ALL_STARS_RABBIT_2,
      [GRRounds.SHIRT]: SpineAnimationIds.GR_HUNTING_ALL_STARS_RABBIT_1,
    },
    ChipAnimations: {
      [GRChipStatuses.LOST]: SpineAnimationIds.GR_HUNTING_ALL_STARS_PASTILLE_LOOSE,
      [GRChipStatuses.PENDING]: SpineAnimationIds.GR_HUNTING_ALL_STARS_PASTILLE_DEFAULT,
      [GRChipStatuses.WON]: SpineAnimationIds.GR_HUNTING_ALL_STARS_PASTILLE_WON,
    },
    Buttons: {
      primary: GRHuntingAllStarsPrimaryButtonBackroundImgPath,
      secondary: GRHuntingAllStarsSecondaryButtonBackroundImgPath,
    },
  },

  [GRSkinIds.GIFT_RACE]: {
    IconPath: GRIconImgPath,
    Backgrounds: {
      locked: {
        [GRRounds.SHOE]: GRRound1BackgroundFallbackImgPath,
        [GRRounds.BALLOON]: GRRound2BackgroundFallbackImgPath,
        [GRRounds.SHIRT]: GRRound3BackgroundFallbackImgPath,
      },

      unlocked: {
        [GRRounds.SHOE]: GRRound1BackgroundImgPath,
        [GRRounds.BALLOON]: GRRound2BackgroundImgPath,
        [GRRounds.SHIRT]: GRRound3BackgroundImgPath,
      },
      rules: GRRulesBackgroundImgPath,
      enrollment: GREnrollmentBackgroundImgPath,
    },
    Logos: {
      [Locales.FR]: GRLogoFR,
      [Locales.ES]: GRLogoES,
      [Locales.DE]: GRLogoDE,
      [Locales.EN]: GRLogoEN,
    },
    Banners: {
      [Locales.FR]: GRBannerFR,
      [Locales.ES]: GRBannerES,
      [Locales.DE]: GRBannerDE,
      [Locales.EN]: GRBannerEN,
    },
    Enrollment: {
      ListItemIconImgPath: GRHoux,
    },
    RoundAnimations: {
      [GRRounds.SHOE]: SpineAnimationIds.GR_SHOE,
      [GRRounds.BALLOON]: SpineAnimationIds.GR_BALLOON,
      [GRRounds.SHIRT]: SpineAnimationIds.GR_SHIRT,
    },
    Buttons: {
      primary: GRPrimaryButtonBackroundImgPath,
      secondary: GRPrimaryButtonBackroundImgPath,
    },
  },
};
